/**
 * Application_Form — pure field-validation logic
 * (application-form-builder spec, task 1.1).
 *
 * This module is the I/O-free heart of the configurable-application validation
 * rules. It holds every decision about whether a single Custom_Field value is
 * well-formed, and whether a whole Application_Response is complete enough to
 * save as a draft or to submit. It is exercised both by the `application-form`
 * Lambda (via `jest` + `fast-check`) and re-used by the frontend `vitest` suite
 * so client-side and server-side validation are *provably the same code* —
 * exactly the "pure core, shared with the frontend" precedent set by
 * `person-service/personLogic.ts` and `division-registry/visibility.ts`.
 *
 * It contains NO AWS SDK calls and NO I/O. Every exported function is total and
 * pure: a given input always produces the same output and never throws for a
 * well-typed argument.
 *
 * What lives here:
 *   - The foundational shared types `ResponseValue`, `FileRef`,
 *     `FieldValidationError` (+ its `code` union), `FormFieldDef`, and the
 *     version-snapshot shapes `SnapshotPage` / `FormVersionSnapshot`. These are
 *     the base types the later pure modules (`formStructure.ts`,
 *     `formVersion.ts`, `boundField.ts`, …) import — this is the lowest module
 *     in the dependency graph, so the types live here and are re-used upward.
 *   - `validateValue` — type/options/limit/format checks for one Custom_Field
 *     value (Req 3.x, 5.2/5.4/5.5/5.6, 7.1/7.2).
 *   - `validateResponse` — whole-response validation honoring per-division
 *     visibility and required-completeness (relaxed for draft, strict for
 *     submit), and asserting that no Bound_Field carries a stored value
 *     (Req 5.3, 6.1, 7.1/7.2/7.3, 17.2/17.6).
 *   - `validateFieldDefinition` — definition-time checks of a field's own
 *     configuration (Req 2.6, 3.2/3.3/3.5).
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 5.2, 5.4, 5.5, 5.6,
 *            7.1, 7.2, 7.3, 17.2, 17.6
 */

import { FieldType } from '../../types/database';

// ---------------------------------------------------------------------------
// Shared value/category types
// ---------------------------------------------------------------------------

/**
 * A reference to a single uploaded file, stored for a FILE_UPLOAD Custom_Field.
 * Mirrors the `DocumentReference`-adjacent shape used by the response item.
 */
export interface FileRef {
  s3Key: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/**
 * The stored value for one Custom_Field within an Application_Response, typed
 * according to the Field_Type (Glossary: Response_Value). A discriminated union
 * so callers narrow on `kind` exhaustively.
 */
export type ResponseValue =
  | { kind: 'text'; value: string } // TEXT, LONG_TEXT, EMAIL, PHONE, DATE(string), single SELECT
  | { kind: 'number'; value: number } // NUMBER
  | { kind: 'boolean'; value: boolean } // single CHECKBOX (boolean)
  | { kind: 'date'; value: string } // DATE — ISO 8601 date string
  | { kind: 'list'; value: string[] } // SELECT/CHECKBOX multi
  | { kind: 'files'; value: FileRef[] }; // FILE_UPLOAD

/**
 * Whether a Form_Field collects/stores its own value (`CUSTOM`) or renders a
 * control backed by a canonical entity and stores nothing in the response
 * (`BOUND`) — Field_Category (Req 17). Defined here as the base type so the
 * bound-field module and the snapshot can share it.
 */
export type FieldCategory = 'CUSTOM' | 'BOUND';

/** The canonical entity a Bound_Field references — Binding_Target (Req 17, 18, 19, 20). */
export type BindingTarget =
  | 'DIVISION'
  | 'TITLE_PREFERENCES'
  | 'CONTACT_IDENTITY'
  | 'DOCUMENT_SLOT'
  // Season selection writes Participation.competitionYear, mirroring DIVISION
  // (director-form-application-cutover, Req 4.1/4.2).
  | 'SEASON';

/**
 * A consumer of a Form_Page or Form_Field — Audience (Req 22). Defined in this
 * base module so the snapshot shape can carry it; `audienceVisibility.ts`
 * re-uses this type.
 */
export type Audience = 'APPLICANT' | 'DIRECTOR' | 'JUDGE';

/**
 * One of the two application stages — Phase (Req 16). Defined here so the
 * snapshot shape can carry it; `phaseGating.ts` re-uses this type.
 */
export type Phase = 'PHASE_1' | 'PHASE_2';

/**
 * Help_Text_Surface formatting indicator (application-builder-rich-help-text
 * Req 2.1). Defined in this base module (mirroring `HelpTextFormat` in
 * `types/database.ts`) so the snapshot shape can carry it. Absent ⇒ PLAIN.
 */
export type HelpTextFormat = 'PLAIN' | 'HTML';

// ---------------------------------------------------------------------------
// Validation-error codes
// ---------------------------------------------------------------------------

/**
 * The full set of field-validation failure codes. The original codes cover the
 * Custom_Field type/options/limit/format rules; the four NEW codes extend the
 * union for the bound-field, division, document-catalog, and contact-intake
 * rules introduced by Requirements 13–23.
 */
export type FieldValidationCode =
  | 'REQUIRED'
  | 'WRONG_TYPE'
  | 'WORD_LIMIT'
  | 'CHAR_LIMIT'
  | 'LIMIT_CONFLICT' // NEW: a LONG_TEXT field set BOTH a word limit and a character limit (application-builder-authoring-fixes, Defect C)
  | 'EMPTY_OPTIONS'
  | 'NOT_AN_OPTION'
  | 'FILE_TYPE'
  | 'FILE_SIZE'
  | 'FILE_COUNT'
  | 'BAD_EMAIL'
  | 'BAD_PHONE'
  | 'BAD_NUMBER'
  | 'BAD_DATE'
  | 'BOUND_VALUE_NOT_ALLOWED' // NEW: a Bound_Field carried a stored value (Req 17.2, 17.6)
  | 'INACTIVE_DIVISION' // NEW: division selection not active (Req 18.4)
  | 'INACTIVE_SEASON' // NEW: season selection not intake-open (director-form-application-cutover Req 4.3)
  | 'MISSING_REQUIRED_DOCUMENT' // NEW: document-catalog gate (Req 20.3)
  | 'CONTACT_FIELD_MISSING'; // NEW: contact-intake required field (Req 14.3)

/** A single validation failure, naming the offending field and (in the message) the rule. */
export interface FieldValidationError {
  fieldId: string;
  code: FieldValidationCode;
  /** Human-readable message identifying the offending field / limit / constraint. */
  message: string;
}

// ---------------------------------------------------------------------------
// Field-definition + snapshot types (base for the later pure modules)
// ---------------------------------------------------------------------------

/**
 * The validation-time view of a Form_Field. A `CUSTOM` field carries a
 * `fieldType` and its per-type `Field_Options`; a `BOUND` field carries a
 * `bindingTarget` and stores no value. Only the attributes the validation rules
 * need are modeled here — the persisted `FormField` (see `types/database.ts`)
 * is a superset.
 */
export interface FormFieldDef {
  id: string;
  label: string;
  fieldCategory: FieldCategory;
  /** Present iff `fieldCategory === 'BOUND'`. */
  bindingTarget?: BindingTarget;
  /** Present iff `fieldCategory === 'CUSTOM'`. */
  fieldType?: FieldType;
  required: boolean;
  // Per-type Field_Options (all optional):
  wordLimit?: number; // LONG_TEXT
  characterLimit?: number; // LONG_TEXT
  options?: string[]; // SELECT / CHECKBOX
  helpText?: string; // remains PLAIN text always (rich-help-text Req 2.6)
  helpTextFormat?: HelpTextFormat; // NEW (rich-help-text) — additive; absent ⇒ PLAIN (Req 5.1)
  helpTextHtml?: string; // NEW (rich-help-text) — sanitized HTML; present iff helpTextFormat === 'HTML'
  allowedContentTypes?: string[]; // FILE_UPLOAD
  maxFileSizeBytes?: number; // FILE_UPLOAD
  maxFileCount?: number; // FILE_UPLOAD
  /** null/empty = visible for all active divisions (the legacy `BOTH` semantics). */
  visibleDivisionKeys?: string[] | null;
  audienceSet?: Audience[];
}

/**
 * A page within a captured Form_Version snapshot, carrying its ordered fields
 * plus the page-level visibility/audience/phase/approval attributes. This is
 * the canonical snapshot shape `formVersion.ts` builds and the validation,
 * gating, and projection modules consume.
 */
export interface SnapshotPage {
  id: string;
  title: string;
  helpText?: string; // remains PLAIN text always (rich-help-text Req 2.6)
  helpTextFormat?: HelpTextFormat; // NEW (rich-help-text) — additive; absent ⇒ PLAIN (Req 5.1)
  helpTextHtml?: string; // NEW (rich-help-text) — sanitized HTML; present iff helpTextFormat === 'HTML'
  autoCollapseHelp?: boolean; // NEW (rich-help-text) — Auto_Collapse_Config (Req 6.5); absent ⇒ false
  displayOrder: number;
  /** null/empty = visible for all active divisions. */
  visibleDivisionKeys?: string[] | null;
  audienceSet: Audience[];
  phase: Phase;
  requiresDirectorApproval: boolean;
  fields: FormFieldDef[];
}

/**
 * An immutable, numbered snapshot of an Application_Form's structure. A response
 * is always interpreted against the snapshot it was captured under (Req 8.3).
 */
export interface FormVersionSnapshot {
  formSchemaId: string;
  versionNumber: number;
  competitionId?: string;
  /** Ordered pages; each carries its ordered fields + visibility/audience/phase/approval. */
  pages: SnapshotPage[];
}

// ---------------------------------------------------------------------------
// Format helpers (pure, total)
// ---------------------------------------------------------------------------

/**
 * A pragmatic e-mail check: exactly one `@`, non-empty local part, and a domain
 * with at least one dot and no whitespace. Deliberately permissive — server and
 * client must agree, so the rule stays simple and total.
 */
function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * A pragmatic phone check: after stripping spaces, dashes, parentheses, dots and
 * a single leading `+`, the remainder must be 7–15 digits (E.164 upper bound).
 */
function isValidPhone(value: string): boolean {
  const stripped = value.trim().replace(/^\+/, '').replace(/[\s\-().]/g, '');
  return /^[0-9]{7,15}$/.test(stripped);
}

/**
 * An ISO-8601 date check: a `YYYY-MM-DD` calendar date (optionally with a time
 * component) that parses to a real calendar date. Rejects impossible dates such
 * as `2024-13-40`.
 */
function isValidIsoDate(value: string): boolean {
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})(T.*)?$/.exec(trimmed);
  if (!match) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/** A positive integer (> 0, no fractional part). Used for word/character limits. */
function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/** Count the words in a string: maximal runs of non-whitespace characters. */
function countWords(text: string): number {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return 0;
  }
  return trimmed.split(/\s+/).length;
}

// ---------------------------------------------------------------------------
// Visibility (applicant-scoped)
// ---------------------------------------------------------------------------

/**
 * Whether a `visibleDivisionKeys` selection means "all divisions" — i.e. it is
 * null, undefined, or contains only blank entries (the legacy `BOTH`
 * semantics). Mirrors `division-registry/visibility.ts#isAllDivisionsVisibility`.
 */
function isAllDivisionsVisibility(
  visibleDivisionKeys: readonly string[] | null | undefined,
): boolean {
  if (visibleDivisionKeys == null) {
    return true;
  }
  return visibleDivisionKeys.every((k) => typeof k !== 'string' || k.trim() === '');
}

/**
 * Whether an element carrying `visibleDivisionKeys` is visible to an applicant
 * assigned to `applicantDivisionKey`. An applicant's own division is, by
 * definition, an active division they are applying to, so the all-divisions
 * case is simply "visible"; a populated list is visible iff it contains the
 * applicant's key (Req 4.2, 4.3). The builder-side full check (which also needs
 * the active-division list) lives in `formStructure.ts#isFieldVisibleForDivision`.
 */
function isVisibleToApplicant(
  visibleDivisionKeys: readonly string[] | null | undefined,
  applicantDivisionKey: string,
): boolean {
  if (isAllDivisionsVisibility(visibleDivisionKeys)) {
    return true;
  }
  return (visibleDivisionKeys as readonly string[]).some(
    (k) => typeof k === 'string' && k.trim() !== '' && k === applicantDivisionKey,
  );
}

// ---------------------------------------------------------------------------
// Answered / emptiness
// ---------------------------------------------------------------------------

/**
 * Whether a Response_Value counts as "answered" for required-completeness.
 * A blank string, an empty list, and an empty file set are unanswered; a number
 * (including 0) and a boolean (including false) are answered.
 */
export function isAnswered(value: ResponseValue | undefined): boolean {
  if (value === undefined) {
    return false;
  }
  switch (value.kind) {
    case 'text':
    case 'date':
      return value.value.trim().length > 0;
    case 'list':
      return value.value.length > 0;
    case 'files':
      return value.value.length > 0;
    case 'number':
    case 'boolean':
      return true;
  }
}

// ---------------------------------------------------------------------------
// validateValue — single Custom_Field value
// ---------------------------------------------------------------------------

/**
 * Validate a single CUSTOM-field value against its field definition. Pure and
 * total.
 *
 * Required-completeness is intentionally NOT checked here (that depends on
 * visibility and the draft/submit mode and is decided in {@link validateResponse});
 * an `undefined` value yields no error. Bound fields never store a value, so a
 * non-CUSTOM field always yields no error here (the no-stored-bound-value
 * invariant is enforced at the response boundary).
 *
 * Checks performed for a present value:
 *   - the value's `kind` matches the Field_Type (else `WRONG_TYPE`);
 *   - SELECT/CHECKBOX membership against `options` (`NOT_AN_OPTION`);
 *   - LONG_TEXT `wordLimit` / `characterLimit` (`WORD_LIMIT` / `CHAR_LIMIT`);
 *   - FILE_UPLOAD content-type / size / count (`FILE_TYPE` / `FILE_SIZE` / `FILE_COUNT`);
 *   - EMAIL / PHONE / NUMBER / DATE formats (`BAD_EMAIL` / `BAD_PHONE` / `BAD_NUMBER` / `BAD_DATE`).
 */
export function validateValue(
  field: FormFieldDef,
  value: ResponseValue | undefined,
): FieldValidationError[] {
  // Bound fields carry no stored value; nothing to validate here.
  if (field.fieldCategory !== 'CUSTOM') {
    return [];
  }
  // Absent value: required-ness is decided by validateResponse, not here.
  if (value === undefined) {
    return [];
  }

  const errors: FieldValidationError[] = [];
  const wrongType = (expected: string): FieldValidationError => ({
    fieldId: field.id,
    code: 'WRONG_TYPE',
    message: `Field "${field.label}" expects a ${expected} value.`,
  });

  switch (field.fieldType) {
    case FieldType.TEXT: {
      if (value.kind !== 'text') {
        errors.push(wrongType('text'));
      }
      break;
    }

    case FieldType.LONG_TEXT: {
      if (value.kind !== 'text') {
        errors.push(wrongType('text'));
        break;
      }
      if (isPositiveInteger(field.wordLimit) && countWords(value.value) > field.wordLimit) {
        errors.push({
          fieldId: field.id,
          code: 'WORD_LIMIT',
          message: `Field "${field.label}" exceeds its word limit of ${field.wordLimit}.`,
        });
      }
      if (
        isPositiveInteger(field.characterLimit) &&
        value.value.length > field.characterLimit
      ) {
        errors.push({
          fieldId: field.id,
          code: 'CHAR_LIMIT',
          message: `Field "${field.label}" exceeds its character limit of ${field.characterLimit}.`,
        });
      }
      break;
    }

    case FieldType.NUMBER: {
      if (value.kind !== 'number') {
        errors.push(wrongType('number'));
        break;
      }
      if (!Number.isFinite(value.value)) {
        errors.push({
          fieldId: field.id,
          code: 'BAD_NUMBER',
          message: `Field "${field.label}" must be a finite number.`,
        });
      }
      break;
    }

    case FieldType.DATE: {
      if (value.kind !== 'date') {
        errors.push(wrongType('date'));
        break;
      }
      if (!isValidIsoDate(value.value)) {
        errors.push({
          fieldId: field.id,
          code: 'BAD_DATE',
          message: `Field "${field.label}" must be an ISO-8601 date (YYYY-MM-DD).`,
        });
      }
      break;
    }

    case FieldType.EMAIL: {
      if (value.kind !== 'text') {
        errors.push(wrongType('text'));
        break;
      }
      if (!isValidEmail(value.value)) {
        errors.push({
          fieldId: field.id,
          code: 'BAD_EMAIL',
          message: `Field "${field.label}" must be a valid email address.`,
        });
      }
      break;
    }

    case FieldType.PHONE: {
      if (value.kind !== 'text') {
        errors.push(wrongType('text'));
        break;
      }
      if (!isValidPhone(value.value)) {
        errors.push({
          fieldId: field.id,
          code: 'BAD_PHONE',
          message: `Field "${field.label}" must be a valid phone number.`,
        });
      }
      break;
    }

    case FieldType.SELECT: {
      // Single-select dropdown: the chosen value must be one of the options.
      if (value.kind !== 'text') {
        errors.push(wrongType('text'));
        break;
      }
      const options = field.options ?? [];
      if (!options.includes(value.value)) {
        errors.push({
          fieldId: field.id,
          code: 'NOT_AN_OPTION',
          message: `Field "${field.label}" value is not one of its configured options.`,
        });
      }
      break;
    }

    case FieldType.CHECKBOX: {
      // CHECKBOX covers a single boolean and a multi-select list.
      if (value.kind === 'boolean') {
        break; // single boolean checkbox — no option-membership to check
      }
      if (value.kind !== 'list') {
        errors.push(wrongType('list or boolean'));
        break;
      }
      const options = field.options ?? [];
      for (const selected of value.value) {
        if (!options.includes(selected)) {
          errors.push({
            fieldId: field.id,
            code: 'NOT_AN_OPTION',
            message: `Field "${field.label}" selection "${selected}" is not one of its configured options.`,
          });
        }
      }
      break;
    }

    case FieldType.FILE_UPLOAD: {
      if (value.kind !== 'files') {
        errors.push(wrongType('file'));
        break;
      }
      const files = value.value;
      if (isPositiveInteger(field.maxFileCount) && files.length > field.maxFileCount) {
        errors.push({
          fieldId: field.id,
          code: 'FILE_COUNT',
          message: `Field "${field.label}" allows at most ${field.maxFileCount} file(s).`,
        });
      }
      const allowed = field.allowedContentTypes;
      for (const file of files) {
        if (allowed && allowed.length > 0 && !allowed.includes(file.contentType)) {
          errors.push({
            fieldId: field.id,
            code: 'FILE_TYPE',
            message: `Field "${field.label}" does not allow files of type "${file.contentType}".`,
          });
        }
        if (
          isPositiveInteger(field.maxFileSizeBytes) &&
          file.sizeBytes > field.maxFileSizeBytes
        ) {
          errors.push({
            fieldId: field.id,
            code: 'FILE_SIZE',
            message: `Field "${field.label}" file "${file.fileName}" exceeds the maximum size of ${field.maxFileSizeBytes} bytes.`,
          });
        }
      }
      break;
    }

    default: {
      // A CUSTOM field with no/unknown fieldType cannot be type-checked here;
      // definition-time validation flags that (see validateFieldDefinition).
      break;
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// validateResponse — whole response
// ---------------------------------------------------------------------------

/**
 * Validate a whole Application_Response against a captured version snapshot.
 *
 * For every CUSTOM field on a page/field that is visible to the applicant's
 * division:
 *   - any present value is validated via {@link validateValue} when
 *     `enforceValueConstraints` is true (the default);
 *   - when `enforceRequired` is true (submit), a required-but-unanswered field
 *     yields `REQUIRED` (Req 5.3, 7.3). When false (draft), required-ness is not
 *     enforced (Req 6.1).
 *
 * `enforceValueConstraints` governs the per-value type/options/limit/format
 * checks (`validateValue`). It defaults to `true` so submit and the client
 * parity schema are unchanged, and the SUBMIT gate always enforces them. The
 * draft-save path passes `false`: a draft is work-in-progress, so a value that
 * is not yet valid (a LONG_TEXT field still over its word/character limit, a
 * half-typed EMAIL/PHONE, an out-of-range NUMBER/DATE) must NEVER block the
 * draft from persisting — otherwise the applicant silently loses edits (the
 * over-limit "Costume Description" auto-save regression). Those constraints are
 * enforced at submit instead (Req 6.1 "relaxed for draft, strict for submit").
 *
 * Regardless of `enforceValueConstraints`, any `fieldId` present in `values`
 * that maps to a Bound_Field is flagged `BOUND_VALUE_NOT_ALLOWED`, enforcing the
 * no-duplication structural invariant at the response boundary (Req 17.2, 17.6)
 * — a draft is relaxed on value *quality* but never on this data-integrity
 * invariant. Bound-field *completeness* is checked against the canonical entity
 * by the caller (see `boundField.ts`).
 *
 * Pure and total; the input `values` map is never mutated.
 */
export function validateResponse(
  snapshot: FormVersionSnapshot,
  values: Record<string, ResponseValue>,
  applicantDivisionKey: string,
  opts: { enforceRequired: boolean; enforceValueConstraints?: boolean; phaseTwoUnlocked?: boolean },
): FieldValidationError[] {
  const errors: FieldValidationError[] = [];
  const boundFieldIds = new Set<string>();
  const customFieldIds = new Set<string>();

  // Phase-aware required-completeness (application-builder-authoring-fixes,
  // Defect A). Defaulting to `true` preserves the exact behavior of every caller
  // that omits the flag ("enforce all phases"); only the submit gates thread the
  // applicant's real unlock state.
  const phaseTwoUnlocked = opts.phaseTwoUnlocked ?? true;

  // Per-value type/options/limit/format enforcement. Defaults to `true` so every
  // existing caller (submit + the client parity schema) is unchanged; the
  // draft-save path passes `false` so an in-progress value that is not yet valid
  // never blocks the draft from persisting (Req 6.1). The no-stored-bound-value
  // invariant below is enforced regardless.
  const enforceValueConstraints = opts.enforceValueConstraints ?? true;

  for (const page of snapshot.pages) {
    const pageVisible = isVisibleToApplicant(page.visibleDivisionKeys, applicantDivisionKey);

    // A PHASE_2 field's required-completeness is enforced only once Phase 2 is
    // unlocked — the same predicate the renderer uses to decide visibility — so
    // a field the applicant cannot yet see never blocks Phase 1 submission.
    const requiredEnforcedForPage = page.phase === 'PHASE_1' || phaseTwoUnlocked;

    for (const field of page.fields) {
      if (field.fieldCategory === 'BOUND') {
        boundFieldIds.add(field.id);
        continue;
      }
      customFieldIds.add(field.id);

      const fieldVisible =
        pageVisible && isVisibleToApplicant(field.visibleDivisionKeys, applicantDivisionKey);

      const value = values[field.id];

      // Type/options/limit/format validity does not depend on visibility or
      // phase, but IS gated by `enforceValueConstraints`: on submit (and the
      // client parity schema) any present value is checked; on a draft save it
      // is skipped so a not-yet-valid value (e.g. over a word/character limit)
      // still persists rather than silently blocking the whole draft (Req 6.1).
      if (enforceValueConstraints) {
        errors.push(...validateValue(field, value));
      }

      // Required-completeness is visibility-, mode-, and phase-scoped.
      if (
        opts.enforceRequired &&
        requiredEnforcedForPage &&
        field.required &&
        fieldVisible &&
        !isAnswered(value)
      ) {
        errors.push({
          fieldId: field.id,
          code: 'REQUIRED',
          message: `Field "${field.label}" is required.`,
        });
      }
    }
  }

  // No Bound_Field id may carry a stored value in `values` (Req 17.2, 17.6).
  for (const fieldId of Object.keys(values)) {
    if (boundFieldIds.has(fieldId)) {
      errors.push({
        fieldId,
        code: 'BOUND_VALUE_NOT_ALLOWED',
        message: `Bound field "${fieldId}" must not store a value in the response; it reads and writes its canonical entity.`,
      });
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// validateFieldDefinition — definition-time configuration checks
// ---------------------------------------------------------------------------

/**
 * Validate a Form_Field's own configuration at definition time (Req 2.6,
 * 3.2/3.3/3.5). Pure and total.
 *
 *   - SELECT / CHECKBOX must declare at least one option (`EMPTY_OPTIONS`).
 *   - A LONG_TEXT `wordLimit` / `characterLimit`, when present, must be a
 *     positive integer (`WORD_LIMIT` / `CHAR_LIMIT`).
 *
 * Field_Type membership against the supported set is checked by
 * `formStructure.ts#validateFieldType`; Bound_Field definition checks live in
 * `boundField.ts`.
 */
export function validateFieldDefinition(field: FormFieldDef): FieldValidationError[] {
  const errors: FieldValidationError[] = [];

  if (field.fieldCategory !== 'CUSTOM') {
    return errors;
  }

  if (field.fieldType === FieldType.SELECT || field.fieldType === FieldType.CHECKBOX) {
    if (!field.options || field.options.length === 0) {
      errors.push({
        fieldId: field.id,
        code: 'EMPTY_OPTIONS',
        message: `Field "${field.label}" of type ${field.fieldType} requires at least one option.`,
      });
    }
  }

  if (field.fieldType === FieldType.LONG_TEXT) {
    if (field.wordLimit !== undefined && !isPositiveInteger(field.wordLimit)) {
      errors.push({
        fieldId: field.id,
        code: 'WORD_LIMIT',
        message: `Field "${field.label}" wordLimit must be a positive integer.`,
      });
    }
    if (field.characterLimit !== undefined && !isPositiveInteger(field.characterLimit)) {
      errors.push({
        fieldId: field.id,
        code: 'CHAR_LIMIT',
        message: `Field "${field.label}" characterLimit must be a positive integer.`,
      });
    }
    // Mutual exclusivity (application-builder-authoring-fixes, Defect C): a
    // LONG_TEXT field may carry a word limit OR a character limit, never both.
    // Keyed on `isPositiveInteger` (a genuinely set positive), so the rule fires
    // only when BOTH are truly set — it composes with, never replaces, the
    // single-limit checks above and is a no-op on a cleared limit (3.9, 3.11).
    if (isPositiveInteger(field.wordLimit) && isPositiveInteger(field.characterLimit)) {
      errors.push({
        fieldId: field.id,
        code: 'LIMIT_CONFLICT',
        message: `Field "${field.label}" cannot set both a word limit and a character limit; choose one.`,
      });
    }
  }

  return errors;
}
